import type { Component, CustomButton, VersionMetadata } from '../api/models/types'

const setUpCommandBar = (flowId: string) => {
  if (!flowId) return

  //* Commandbar help is temporarily disabled
  window.CommandBar.addMetadata('displayHelp', false)

  //*----------------- PAGES & COMPONENTS-----------------//
  function loadCommandBarPagesRecords() {
    const flowId = window.Savvy.getFlowSelection && window.Savvy.getFlowSelection().flowId
    if (!flowId) return

    const flowJson = window.Savvy.getFlowJson && window.Savvy.getFlowJson(flowId)

    let pages: Array<{ label: string; id: string; componentsData: string }> = []
    ;(flowJson?.pages || []).forEach((page) => {
      pages.push({
        label: page.key,
        id: page.id,
        componentsData: (page.components || []).reduce((string, component) => {
          string += component.key + '-'
          if (component.text) string += component.text + '-'
          if (component.label) string += component.label + '-'
          if ((component as CustomButton).description)
            string += (component as CustomButton).description + '-'
          return string
        }, ''),
      })
    })

    return pages
  }
  window.CommandBar.addRecords('Pages', []) // call once with an empty array to prevent fetch on first page load
  window.CommandBar.addRecords('Pages', loadCommandBarPagesRecords)

  function loadCommandBarComponentsRecords() {
    const flowId = window.Savvy.getFlowSelection && window.Savvy.getFlowSelection().flowId
    if (!flowId) return

    const flowJson = window.Savvy.getFlowJson && window.Savvy.getFlowJson(flowId)

    let components: Array<{
      label: string
      id: string
      key: string
      type: string
      pageId: string
      componentsData: string
    }> = []

    ;(flowJson?.pages || []).forEach((page) => {
      ;(page.components || []).forEach((component: Component) => {
        components.push({
          label: component.key,
          id: component.id,
          key: component.key,
          type: component.type,
          pageId: page.id,
          componentsData:
            (component.text || '') +
            '-' +
            (component.label || '') +
            '-' +
            ((component as CustomButton).description || ''),
        })
      })
    })
    ;(flowJson?.components || []).forEach((component) => {
      components.push({
        label: component.key,
        id: component.id,
        key: component.key,
        type: component.type,
        pageId: 'global',
        componentsData:
          (component.text || '') +
          '-' +
          (component.label || '') +
          '-' +
          ((component as CustomButton).description || ''),
      })
    })

    return components
  }

  window.CommandBar.addRecords('Components', []) // call once with an empty array to prevent fetch on first page load
  window.CommandBar.addRecords('Components', loadCommandBarComponentsRecords)

  function goToPageCallback(page) {
    window.Savvy.goToPage && window.Savvy.goToPage(flowId, page.page.id)
  }

  window.CommandBar.addCallback('go_to_page', goToPageCallback)

  function findComponent(data: any) {
    let component = data.record
    if (component.pageId === 'global') {
      //TODO select global component
    } else {
      window.Savvy.goToPage && window.Savvy.goToPage(flowId, component.pageId)
      //TODO select component
    }
  }

  window.CommandBar.addCallback('find_component', findComponent)

  //*----------------- USERDATA -----------------//
  function loadCommandBarUserDataRecord() {
    const flowId = window.Savvy.getFlowSelection && window.Savvy.getFlowSelection().flowId
    if (!flowId) return

    const userData = window.Savvy.getUserData && window.Savvy.getUserData(flowId)
    if (!userData) return

    let userDataRecords: Array<{ label: string; id: string }> = []

    Object.keys(userData).forEach((key) => {
      userDataRecords.push({
        label: key + ' = ' + userData[key],
        id: key,
      })
    })

    return userDataRecords
  }

  window.CommandBar.addRecords('UserData', []) // call once with an empty array to prevent fetch on first page load
  window.CommandBar.addRecords('UserData', loadCommandBarUserDataRecord)

  function editUserData(data: any) {}

  window.CommandBar.addCallback('edit_user_data', editUserData)

  //*----------------- RESET USERDATA -----------------//
  function resetUserData() {
    const id = window.Savvy.getFlowSelection && window.Savvy.getFlowSelection().flowId
    if (!id) return

    window.Savvy.resetUserData && window.Savvy.resetUserData(id)
  }
  window.CommandBar.addCallback('reset_userdata', resetUserData)

  //*----------------- SWITCH TO VERSION -----------------//
  async function loadCommandBarVersionsRecords() {
    const flowId = window.Savvy.getFlowSelection && window.Savvy.getFlowSelection().flowId
    if (!flowId) return

    if (window.Savvy.getVersionHistory) {
      let versionsData = await window.Savvy.getVersionHistory(flowId, 1)

      let latestVersionData: VersionMetadata | undefined =
        (versionsData && versionsData.versions && versionsData.versions[0]) || undefined
      if (!latestVersionData) return

      let versionsNumberList: number[] = []
      for (let i = latestVersionData.version; i >= 0; i--) {
        versionsNumberList.push(i)
      }

      return ['latest', 'prod', 'staging', ...versionsNumberList].map((version) => ({
        label:
          version === 'prod'
            ? 'Production'
            : version === 'staging'
            ? 'Staging'
            : version === 'latest'
            ? 'Latest'
            : version,
        id: version,
      }))
    }
  }
  window.CommandBar.addRecords('Versions', []) // call once with an empty array to prevent fetch on first page load
  window.CommandBar.addRecords('Versions', loadCommandBarVersionsRecords)

  function switchToVersion(data: any) {
    if (window.Savvy.switchToVersion) {
      window.Savvy.switchToVersion(flowId, data.version.id)
    }
  }
  window.CommandBar.addCallback('switch_to_version', switchToVersion)

  //*----------------- EXPERIMENTS -----------------//
  function loadCommandBarExperimentsRecords() {
    const flowId = window.Savvy.getFlowSelection && window.Savvy.getFlowSelection().flowId
    if (!flowId) return

    const flowJson = window.Savvy.getFlowJson && window.Savvy.getFlowJson(flowId)

    if (!flowJson || !flowJson.experiments || !flowJson.experiments.length) return

    let experiments: Array<{
      label: string
      id: string
      key: string
      variants: Array<{ label: string; id: string; key: string }>
    }> = []

    flowJson.experiments.forEach((experiment) => {
      experiments.push({
        label: experiment.key,
        id: experiment.id,
        key: experiment.key,
        variants: experiment.variants.map((variant) => ({
          label: variant.key,
          id: variant.id,
          key: variant.key,
        })),
      })
    })

    return experiments
  }

  window.CommandBar.addRecords('Experiments', []) // call once with an empty array to prevent fetch on first page load
  window.CommandBar.addRecords('Experiments', loadCommandBarExperimentsRecords)

  function selectExperiment(data: any) {
    let variants = data.Experiments.variants
    if (!variants || !variants.length) return

    //* I add the possible variants to the command bar as arguments
    window.CommandBar.addArgumentChoices(
      'Variants',
      variants.map((variant: any) => ({ ...variant, experiment: data.Experiments.key }))
    )

    //* I execute the command to select the variant (cb: set_variant)
    window.CommandBar.execute('292912')
  }

  function setVariant(data: any) {
    let variant = data.variant
    if (variant) {
      window.Savvy.setUserData &&
        window.Savvy.setUserData(flowId, { [variant.experiment]: variant.key })
    }
    window.CommandBar.addArgumentChoices('Variants', [])
  }

  window.CommandBar.addCallback('select_experiment', selectExperiment)
  window.CommandBar.addCallback('set_variant', setVariant)
}

export default setUpCommandBar
